/**
 * Canonical record-link contract (REG-592).
 *
 * A shared URL identifies *which record* is open, never *how* it is presented:
 *
 *   /{routeName}?record={tableName}:{recordId}[&tab={subState}]
 *
 * A modal opened on top of a full-page detail (funder/rep/lead source) keeps the page's
 * target in `record` and carries its own target in `overlay`:
 *
 *   /{routeName}?record={pageTable}:{id}&overlay={modalTable}:{id}
 *
 * Without the second key, the modal would steal `record` from the page, which
 * flipped the page back to its list view the moment the modal opened.
 *
 * The presentation registry below translates that target into a side panel, a
 * modal, or a full-page detail. Callers never pick a presentation themselves.
 *
 * Pure module — no React, no router. Import from `useRecordNavigator` for the
 * hook-shaped API.
 */

/** How a table's records are presented once their target is resolved. */
export type RecordPresentation = 'panel' | 'modal' | 'page';

/**
 * Per-table presentation. Tables absent from this map fall back to
 * DEFAULT_PRESENTATION, which matches how every non-merchant record behaves
 * today (the linked-record modal over whatever page you were on).
 */
export const RECORD_PRESENTATION: Record<string, RecordPresentation> = {
  merchant: 'panel',
  submission: 'modal',
  funder: 'page',
  rep: 'page',
  // REG-636: lead sources open their own full detail page like reps and
  // funders; its fields edit in the record modal carried over that page.
  lead_source: 'page',
};

export const DEFAULT_PRESENTATION: RecordPresentation = 'modal';

export function presentationFor(tableName: string): RecordPresentation {
  return RECORD_PRESENTATION[tableName] ?? DEFAULT_PRESENTATION;
}

/** A logical record target: what to open, plus optional UI sub-state. */
export interface RecordTarget {
  tableName: string;
  id: string;
  /** Presentation sub-state, e.g. the merchant panel's inner tab. */
  tab?: string;
}

/** Canonical query key holding the record target. */
export const RECORD_PARAM = 'record';
/** Query key holding a modal target opened over a page-presentation detail. */
export const OVERLAY_PARAM = 'overlay';
/**
 * Canonical query key holding presentation sub-state.
 *
 * Namespaced rather than a bare `tab` because dashboards already own `?tab=`
 * for their own active tab — a record opened from a dashboard must not clobber
 * it, and a dashboard's tab must not be read as a merchant panel tab.
 */
export const TAB_PARAM = 'record_tab';

/**
 * Pre-REG-592 query keys still found in bookmarks and shared links.
 * `panel` carried a `table:id` pair; the rest carried a bare id for one table.
 */
const LEGACY_PAIR_PARAMS = ['panel'] as const;
const LEGACY_ID_PARAMS: Record<string, string> = {
  funder: 'funder',
  rep: 'rep',
};
/** The legacy `?panel=` link kept its inner tab in the generic `tab` key. */
const LEGACY_TAB_PARAM = 'tab';

/**
 * Every key `withRecordTarget`/`withoutRecordTarget` owns unconditionally.
 * Anything else in the query string is preserved.
 */
const OWNED_PARAMS = [
  RECORD_PARAM,
  OVERLAY_PARAM,
  TAB_PARAM,
  ...LEGACY_PAIR_PARAMS,
  ...Object.keys(LEGACY_ID_PARAMS),
];

/**
 * Strip every key this module owns. `tab` goes only when it came in alongside a
 * legacy `panel` link, since it belongs to other pages otherwise.
 */
function clearOwnedParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  const hadLegacyPanel = LEGACY_PAIR_PARAMS.some((key) => next.has(key));
  for (const key of OWNED_PARAMS) next.delete(key);
  if (hadLegacyPanel) next.delete(LEGACY_TAB_PARAM);
  return next;
}

/**
 * Legacy `?panel=` accepted both singular and plural table spellings.
 * Normalize to the singular table name used by metadata.
 */
function normalizeLegacyTableName(value: string): string {
  const lower = value.toLowerCase();
  return lower === 'merchants' ? 'merchant' : lower;
}

/** A record id is opaque here, but must be non-empty and free of the ':' separator. */
function parseTargetValue(value: string): { tableName: string; id: string } | null {
  const separator = value.indexOf(':');
  if (separator <= 0) return null;
  const tableName = value.slice(0, separator).trim();
  const id = value.slice(separator + 1).trim();
  if (!tableName || !id) return null;
  return { tableName, id };
}

/**
 * Read the record target out of a URL's query string, accepting both the
 * canonical `record` key and the legacy keys. Returns null when no record is
 * targeted. Shape validation only — whether the table exists and the caller may
 * see the record is the resolver's job.
 */
export function parseRecordTarget(params: URLSearchParams): RecordTarget | null {
  const tab = params.get(TAB_PARAM) || undefined;

  const canonical = params.get(RECORD_PARAM);
  if (canonical) {
    const parsed = parseTargetValue(canonical);
    return parsed ? { ...parsed, tab } : null;
  }

  for (const key of LEGACY_PAIR_PARAMS) {
    const value = params.get(key);
    if (!value) continue;
    const parsed = parseTargetValue(value);
    if (!parsed) continue;
    return {
      tableName: normalizeLegacyTableName(parsed.tableName),
      id: parsed.id,
      tab: tab || params.get(LEGACY_TAB_PARAM) || undefined,
    };
  }

  for (const [key, tableName] of Object.entries(LEGACY_ID_PARAMS)) {
    const value = params.get(key)?.trim();
    if (value) return { tableName, id: value, tab };
  }

  return null;
}

/** True when the target came from a legacy key and should be rewritten in place. */
export function isLegacyRecordParam(params: URLSearchParams): boolean {
  if (params.has(RECORD_PARAM)) return false;
  return OWNED_PARAMS.some((key) => key !== RECORD_PARAM && key !== TAB_PARAM && params.has(key));
}

/**
 * Return a copy of `params` carrying `target`, leaving every unrelated
 * search/filter parameter untouched.
 */
export function withRecordTarget(params: URLSearchParams, target: RecordTarget): URLSearchParams {
  const next = clearOwnedParams(params);
  next.set(RECORD_PARAM, `${target.tableName}:${target.id}`);
  if (target.tab) next.set(TAB_PARAM, target.tab);
  return next;
}

/**
 * Return a copy of `params` with the record target removed, preserving
 * everything else. Used when a panel or modal closes.
 */
export function withoutRecordTarget(params: URLSearchParams): URLSearchParams {
  return clearOwnedParams(params);
}

/**
 * Read the overlay target — a modal opened over a page-presentation detail.
 * No legacy spellings and no `tab` sub-state: the overlay key is new and only
 * ever written in canonical form.
 */
export function parseOverlayTarget(params: URLSearchParams): RecordTarget | null {
  const value = params.get(OVERLAY_PARAM);
  return value ? parseTargetValue(value) : null;
}

/**
 * Return a copy of `params` carrying `target` in the overlay key, leaving the
 * page's own `record` target and every unrelated parameter untouched.
 */
export function withOverlayTarget(params: URLSearchParams, target: RecordTarget): URLSearchParams {
  const next = new URLSearchParams(params);
  next.set(OVERLAY_PARAM, `${target.tableName}:${target.id}`);
  return next;
}

/**
 * Return a copy of `params` with only the overlay target removed. Used when a
 * modal over a page detail closes — the page's `record` target stays put.
 */
export function withoutOverlayTarget(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(OVERLAY_PARAM);
  return next;
}

/**
 * Serialize a query string, leaving the `table:id` separator readable.
 * A literal ':' is legal in a query string, and these URLs are meant to be
 * pasted into chat by hand.
 */
export function serializeParams(params: URLSearchParams): string {
  return params.toString().replace(/%3A/g, ':');
}

/** Build the canonical path for a target on an already-resolved route name. */
export function buildRecordPath(
  routeName: string,
  target: RecordTarget,
  baseParams?: URLSearchParams,
): string {
  const search = serializeParams(withRecordTarget(baseParams ?? new URLSearchParams(), target));
  return `/${routeName}${search ? `?${search}` : ''}`;
}
